/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useCallback, useMemo } from 'react';
import { useFeatureFlag } from '@/features/globalConfig';
import { useBuyAgainPurchases } from '@/features/orderHistory/api/getBuyAgainPurchases';
import { getFormattedDateRange } from '@/features/orderHistory/utils/getFormattedDateRange';
import { useAppData } from '@/hooks/useAppData';
import { type BuyAgainPurchase } from '@/features/orderHistory';
import { shipmentStatusTypes } from '@/constants/shipmentStatusTypes';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';

export function usePreviouslyBoughtItem() {
  const enablePurchasedDate = useForteDecisionFlagWithTrueDefault('enablePurchasedDate');
  const buyAgainEndpointEnabled = useFeatureFlag('BUY_AGAIN_ENDPOINT_ENABLED') === 'true';
  const { appData } = useAppData();
  const { userAuthenticated } = appData;
  const shouldCallPurchaseSummary =
    !!enablePurchasedDate && buyAgainEndpointEnabled && userAuthenticated;

  const dateRange = Number(useFeatureFlag('BUY_AGAIN_DATE_RANGE'));
  const currentDate = new Date();
  const { fromDate, toDate } = getFormattedDateRange({
    date: currentDate,
    dateRange,
  });

  const { data: buyAgainData } = useBuyAgainPurchases({
    fromDate,
    toDate,
    enabled: shouldCallPurchaseSummary,
  });

  const purchaseMap = useMemo(
    () =>
      new Map<string, BuyAgainPurchase>(
        (buyAgainData?.purchases ?? [])
          .filter(
            (purchase) =>
              (purchase.orderStatus === shipmentStatusTypes.noPendingAction ||
                purchase.orderStatus === shipmentStatusTypes.inStorePicked) &&
              Number(purchase.quantity) - Number(purchase.returnedQuantity) > 0,
          )
          .reduce((map, purchase) => {
            if (!map.has(purchase.skuId)) {
              map.set(purchase.skuId, purchase);
            }
            return map;
          }, new Map<string, BuyAgainPurchase>()),
      ),
    [buyAgainData],
  );

  return useCallback(
    (skuId: string | undefined) => {
      return shouldCallPurchaseSummary && skuId ? purchaseMap.get(skuId) : undefined;
    },
    [shouldCallPurchaseSummary, purchaseMap],
  );
}
